import { useEffect, useState } from "react";
import { weeklyProfit } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { useBranch } from "@/context/BranchContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Kpi, Th, MONEY_COLS, MoneyCells, ProfitCell } from "./ProfitUI";
import { ScopeSelect, scopeParam } from "./ScopeSelect";

export function WeeklyProfitTab() {
  const { branchId } = useBranch();
  const [scope, setScope] = useState("current");
  const [weeks, setWeeks] = useState(8);
  const [endDate, setEndDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    weeklyProfit({ weeks, end_date: endDate, ...scopeParam(scope) })
      .then(setData)
      .finally(() => setLoading(false));
  }, [weeks, endDate, branchId, scope]);

  const t = data?.totals || {};
  const cols = MONEY_COLS.length + 3;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <ScopeSelect value={scope} onChange={setScope} testId="weekly-scope-select" />
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Weeks</Label>
          <Select value={String(weeks)} onValueChange={(v) => setWeeks(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="profit-weeks-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {[4, 8, 12, 26, 52].map((n) => <SelectItem key={n} value={String(n)}>Last {n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Ending</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" data-testid="profit-end-date-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 tabular">
        <Kpi label="Total Commission" value={t.total_commission} testId="weekly-kpi-commission" />
        <Kpi label="Ciboee Commission" value={t.ciboee_commission} tone="warning" testId="weekly-kpi-ciboee" />
        <Kpi label="DC Loss" value={t.dc_loss} tone="warning" testId="weekly-kpi-dc-loss" />
        <Kpi label="Min Guarantee" value={t.min_guarantee} tone="warning" testId="weekly-kpi-mg" />
        <Kpi label="Final Profit" value={t.final_profit} tone={t.final_profit >= 0 ? "positive" : "danger"} big testId="weekly-kpi-profit" />
      </div>
      <div className="bg-white rounded-xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <Th>Week</Th><Th className="text-right">Days</Th>
              {MONEY_COLS.map(([k, l]) => <Th key={k} className="text-right">{l}</Th>)}
              <Th className="text-right">Final Profit</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={cols} className="px-4 py-6 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && (data?.weeks || []).map((w) => (
              <tr key={w.week_start} className="border-b border-zinc-100 last:border-b-0 tabular" data-testid={`profit-week-${w.week_start}`}>
                <td className="px-3 py-2.5 font-medium whitespace-nowrap">{w.label}</td>
                <td className="px-3 py-2.5 text-right text-zinc-500">{w.days_with_data}</td>
                <MoneyCells row={w} />
                <ProfitCell value={w.final_profit} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
